/**
 * Alpine.js Input Editor Component
 * Provides inline editing functionality for single-line and multi-line input fields
 */

import { editorBase } from './alpine-editor-base.js';

/**
 * Input Editor Component Factory
 * Handles input fields like occupation, education_desc, website
 */
export function inputEditor() {
    return {
        // State
        isEditing: false,
        value: '',
        originalValue: '',
        isSaving: false,
        error: null,
        charCount: 0,

        // Configuration (set from data attributes or defaults)
        fieldName: '',
        maxLength: 1000,
        placeholder: '',
        endpoint: '',
        isMultiline: false, // For website field
        
        // Computed properties
        get hasChanges() {
            return this.value !== this.originalValue;
        },
        
        get remainingChars() {
            return this.maxLength - this.charCount;
        },
        
        get displayValue() {
            if (!this.value) {
                return `<span class="text-gray-400 italic">Click to add ${this.fieldName}</span>`;
            }
            
            // For website field, handle multiple URLs
            if (this.fieldName === 'website') {
                return this.value.split('\n')
                    .filter(url => url.trim())
                    .map(url => `<a href="${url.startsWith('http') ? url : 'https://' + url}" target="_blank" class="text-sky-600 hover:text-sky-800">${url}</a>`)
                    .join('<br>');
            }
            
            // For other fields, just escape HTML
            return editorBase.escapeHtml(this.value);
        },
        
        // Methods
        init() {
            // Initialize from element data attributes
            const element = this.$el;
            
            this.fieldName = element.dataset.field || this.fieldName;
            this.maxLength = parseInt(element.dataset.maxLength) || this.maxLength;
            this.placeholder = element.dataset.placeholder || this.placeholder;
            this.isMultiline = element.dataset.multiline === 'true';
            
            // Get endpoint based on field
            this.endpoint = element.dataset.endpoint || editorBase.getEndpointForField(this.fieldName);
            
            // Get initial value from element's data attributes
            let rawValue = element.dataset.rawValue || element.dataset.value || '';
            
            // If no value on element, check for .inline-edit-content child (legacy)
            if (!rawValue) {
                const contentEl = element.querySelector('.inline-edit-content');
                if (contentEl) {
                    rawValue = contentEl.dataset.rawValue || contentEl.dataset.value || '';
                }
            }
            
            // Decode HTML entities if the value came from a data attribute
            if (rawValue) {
                rawValue = editorBase.decodeHtml(rawValue);
            }
            
            // If still no value, try to get from store
            if (!rawValue) {
                rawValue = this.$store.profile?.getFieldValue(this.fieldName) || '';
            }
            
            this.originalValue = rawValue;
            this.value = this.originalValue;
            
            this.charCount = this.value.length;
            
            // Initialize base utilities
            editorBase.initEditor(this);
        },

        startEdit() {
            this.isEditing = true;
            this.error = null;
            
            // Register with store for tracking
            if (this.$store.profile) {
                this.$store.profile.startEditing(this.fieldName);
            }
            
            // Focus the input after Alpine updates the DOM
            this.$nextTick(() => {
                const input = this.$refs.input;
                if (input) {
                    input.focus();
                    if (input.tagName === 'INPUT') {
                        input.select();
                    }
                }
            });
        },

        cancel() {
            this.value = this.originalValue;
            this.charCount = this.value.length;
            this.error = null;
            this.completeEdit();
        },

        completeEdit() {
            this.isEditing = false;
            
            // Unregister from store
            if (this.$store.profile) {
                this.$store.profile.stopEditing(this.fieldName);
            }
        },

        updateCharCount() {
            this.charCount = this.value.length;
        },

        handleKeydown(event) {
            if (event.key === 'Escape') {
                this.cancel();
            } else if (event.key === 'Enter' && !this.isMultiline) {
                event.preventDefault();
                this.save();
            } else if (event.key === 'Enter' && event.ctrlKey && this.isMultiline) {
                event.preventDefault();
                this.save();
            }
        },

        async save() {
            if (!this.hasChanges || this.isSaving) {
                return;
            }
            
            this.isSaving = true;
            this.error = null;
            
            try {
                // Save to server and wait for response
                const result = await editorBase.saveField(this.endpoint, this.fieldName, this.value);
                
                // Save successful - update local state
                this.originalValue = this.value;
                
                // Update store
                if (this.$store.profile) {
                    this.$store.profile.updateField(this.fieldName, this.value);
                }
                
                // Exit edit mode
                this.completeEdit();
                
            } catch (error) {
                // Show error and keep in edit mode
                this.error = error.message || (window.t ? window.t('common.save_failed') : 'Failed to save changes. Please try again.');
                console.error('Save error:', error);
            } finally {
                this.isSaving = false;
            }
        }
    }
}